import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TextField } from '@elementary/common-ui/components';
import { IncidentSummary as IncidentSummaryType } from 'incidents/types';
import useIncidentSummary from '../hooks/useIncidentSummary';

const PLACEHOLDER = 'Add a summary...';
const MAX_ROWS = 10;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    padding: 0;
    background-color: transparent;
    border: none !important;

    &:hover,
    &.Mui-focused {
      border: none !important;
    }
  }

  .MuiOutlinedInput-root {
    border: none !important;

    .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }

    &:hover .MuiOutlinedInput-notchedOutline,
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: none !important;
    }
  }

  .MuiInputBase-input {
    padding: 0;
    font-size: 14px;
    line-height: 1.5;

    &::placeholder {
      color: ${({ theme }) => theme.palette.common.slate};
      opacity: 1;
    }
  }
`;

type Props = {
  incidentId: string;
  summaries: IncidentSummaryType[];
};

const IncidentSummary = ({ incidentId, summaries }: Props) => {
  const { userSummary, saveSummary, removeSummary, canEdit } =
    useIncidentSummary({
      incidentId,
      summaries,
    });

  const [value, setValue] = useState(userSummary?.content || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(userSummary?.content || '');
  }, [userSummary?.content]);

  const handleBlur = () => {
    const trimmedValue = value.trim();
    const originalContent = userSummary?.content || '';

    if (trimmedValue === originalContent) {
      return;
    }

    if (trimmedValue) {
      saveSummary(trimmedValue);
    } else if (userSummary) {
      removeSummary();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setValue(userSummary?.content || '');
      inputRef.current?.blur();
    }
  };

  return (
    <StyledTextField
      inputRef={inputRef}
      fullWidth
      multiline
      maxRows={MAX_ROWS}
      value={value}
      placeholder={PLACEHOLDER}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      readOnly={!canEdit}
    />
  );
};

export default IncidentSummary;
